var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner, Box, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { config } from 'lib-shared-backend-config';
import { useCallback, useState } from 'react';
import { billingApi } from 'apis/billing';
import { useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useCurrentUser } from 'utils/user/useCurrentUser';
import styles from './addPaymentMethodModal.module.scss';
/** A component that displays a modal for adding a new payment method. */
export const AddPaymentMethodModal = createAppModal({ type: 'settings:addPaymentMethod' }, ({ isOpen, meta, hide }) => {
    const notify = useNotifications();
    const stripe = useStripe();
    const elements = useElements();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stripeError, setStripeError] = useState();
    const { user: { email: loggedInUserEmail }, } = useCurrentUser();
    const isStripeInTestMode = config.sdk.stripe.startsWith('pk_test');
    const [updateCreditCardToken] = billingApi.useCreateCreditCardMutation();
    const handleConfirm = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        event === null || event === void 0 ? void 0 : event.preventDefault();
        if (!event ||
            !stripe ||
            !elements ||
            !elements.getElement(CardElement) ||
            !loggedInUserEmail) {
            return;
        }
        setIsSubmitting(true);
        const { error: createTokenError, token } = yield stripe.createToken(elements.getElement(CardElement), {
            name: loggedInUserEmail,
        });
        if (createTokenError) {
            setStripeError(createTokenError);
        }
        if (token) {
            try {
                const { data } = yield updateCreditCardToken({
                    token: { token },
                }).unwrap();
                notify({
                    title: 'Success',
                    text: `Payment method successfully added`,
                    icon: 'done',
                    className: 'success',
                    time: 3000,
                    showDesktop: false,
                });
                meta === null || meta === void 0 ? void 0 : meta.resolve({ token, paymentId: data.external_key });
            }
            catch (error) {
                const errorMessage = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.data) === null || _a === void 0 ? void 0 : _a.errors) === null || _b === void 0 ? void 0 : _b[0]) !== null && _c !== void 0 ? _c : `Please try again or contact support.`;
                notify({
                    title: 'Failed to add payment method',
                    text: errorMessage,
                    icon: 'exclamation',
                    className: 'danger',
                    time: 3000,
                    showDesktop: false,
                });
            }
        }
        setIsSubmitting(false);
        handleCancel();
    });
    const handleCancel = useCallback(() => {
        setStripeError(undefined);
        hide === null || hide === void 0 ? void 0 : hide();
    }, [hide]);
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: handleCancel, className: styles.modal }, { children: [_jsx(ModalHead, { title: "Add new payment method", onClose: handleCancel }), _jsxs(ModalBody, { children: [stripeError && (_jsx(Box, Object.assign({ margin: "0 0 300" }, { children: _jsx(Banner, { variant: "fail", description: _jsx(Text.Paragraph, { children: stripeError.message }) }) }))), isStripeInTestMode && (_jsx(Box, Object.assign({ padding: "200 0" }, { children: _jsx(Text.Paragraph, Object.assign({ subdued: true }, { children: "Test card: 4000002500003155" })) }))), _jsx("form", Object.assign({ onSubmit: handleConfirm }, { children: _jsx(CardElement, { className: styles.stripeCardElement, options: {
                                disabled: isSubmitting,
                            } }) }))] }), _jsx(ModalFoot, { confirmOnClick: handleConfirm, cancelOnClick: handleCancel, confirmName: "Add payment method", cancelName: "Cancel", confirmProps: {
                    disabled: false,
                    isLoading: isSubmitting,
                } })] })));
});
